// File: src/pages/StockReports.jsx
import React, { useEffect, useState } from "react";
import { Card, Input, Button, Spinner, Label } from "reactstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// ✅ adjust these component imports as per your project
import { PurchaseProductList_Product } from "../import/ApiEndPoint/Api";
import { createOrderhistoryview } from "../import/ApiEndPoint/ApiCalling";

import ClosingStockReport from "./ClosingStock";
import LowStockAlert from "./LowStockAlert";
import DamagedStock from "./DamagedStock";
import OverDueStock from "./OverdueStock";
import StockDifferenceReport from "./StockDifference";
import DeadPartyReport from "./DeadPartyReport";
import WareHouseOverDueStock from "./WareHouseOverDueStock";
import Wastageproduction from "./Wastageproduction";

/* ---------------- TAB UI CSS (same like MasterTabs) ---------------- */
const CSS_TABS = `
  .ul-shell{
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  .ul-tabsbar{
    width: 100%;
    background: #ffffff;
    border-bottom: 1px solid #eef2f7;
    padding: 10px 12px;
  }

  .ul-tabswrap{
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
  }

  .ul-tabs{
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 0;
    position: relative;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .ul-tab{
    flex: 1 1 0;
    text-align: center;
    border: none;
    background: transparent;
    padding: 10px 8px;
    font-weight: 900;
    font-size: 13px;
    color: #0f172a;
    cursor: pointer;
    position: relative;
    user-select: none;
    transition: color .2s ease, transform .15s ease;
    white-space: nowrap;
  }

  .ul-tab:hover{ transform: translateY(-1px); }
  .ul-tab.active{ color: #ffffff; }

  .ul-indicator{
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: 14px;
    background: linear-gradient(90deg, #2563eb, #1d4ed8);
    transition: transform .28s cubic-bezier(.2,.8,.2,1);
    box-shadow: 0 10px 18px rgba(37,99,235,0.22);
  }

  @keyframes ulPop {
    0% { transform: scale(.96); }
    100% { transform: scale(1); }
  }
  .ul-tab.active{ animation: ulPop .18s ease; }

  .count-pill{
    background:#eef2ff;
    color:#3730a3;
    font-weight:900;
    padding:6px 10px;
    border-radius:999px;
  }

  .sr-table-wrap{
    width:100%;
    overflow:auto;
    border:1px solid #e5e7eb;
    border-radius:14px;
    background:#fff;
  }
  .sr-table{
    width:100%;
    border-collapse:collapse;
    min-width:1200px;
  }
  .sr-table thead th{
    background:#f4f7f6;
    color:#0f172a;
    font-weight:800;
    text-transform:uppercase;
    font-size:12px;
    letter-spacing:.03em;
    border-bottom:1px solid #e5e7eb;
    padding:8px 10px;
    position:sticky;
    top:0;
    z-index:1;
    text-align:center;
    white-space:nowrap;
  }
  .sr-table tbody td{
    padding:8px 10px;
    border-bottom:1px solid #f1f5f9;
    color:#0f172a;
    font-size:14px;
    text-align:center;
    vertical-align:middle;
  }
  .sr-table tbody tr:hover{ background:#f9fbff; }

  .stock-filter-box{
    display:flex;
    align-items:flex-end;
    gap:8px;
    flex-wrap:wrap;
  }
`;

function injectOnce() {
  if (document.getElementById("stockreports-inline-css")) return;
  const style = document.createElement("style");
  style.id = "stockreports-inline-css";
  style.innerHTML = CSS_TABS;
  document.head.appendChild(style);
}

/* ---------------- TABS ---------------- */
const TABS = [
  { key: "all", label: "All in one" },
  { key: "opening", label: "Opening Stock" },
  { key: "inward", label: "Inward Stock" },
  { key: "outward", label: "Outward Stock" },
  { key: "closing", label: "Closing Stock" },
  { key: "tab5", label: "Low Stock" },
  { key: "tab6", label: "Damaged Stock" },
  { key: "tab7", label: "Over Due Stock" },
  { key: "tab8", label: "Stock Difference" },
  { key: "tab9", label: "Dead Stock" },
  { key: "tab10", label: "Dead Party" },
  { key: "tab11", label: "Wastage" },
];

/* ---------------- PROFIT COLOR LOGIC ---------------- */
const defaultColorConfig = {
  green: "#75fa8e",
  orange: "#d9614c",
  blue: "#87c8fa",
  red: "#f78981",
};

function getProfitColor(profitPercentage, colorConfig) {
  const raw = profitPercentage ?? "";
  const normalized = String(raw).replace(/[^\d.-]/g, "");
  const p = normalized === "" ? 0 : Number(normalized) || 0;

  const merged = {
    ...defaultColorConfig,
    ...(colorConfig || {}),
  };

  if (p >= 15) return merged.green;
  if (p >= 10) return merged.orange;
  if (p >= 5) return merged.blue;
  return merged.red;
}

export default function StockReports() {
  const BASE_URL = "https://customer-node.rupioo.com";

  const [activeTab, setActiveTab] = useState("all");

  const [reportType, setReportType] = useState("opening");
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [productAvgSaleRateMap, setProductAvgSaleRateMap] = useState({});

  const [colorConfig, setColorConfig] = useState(defaultColorConfig);

  // outward date filter only
  const [outwardStartDate, setOutwardStartDate] = useState("");
  const [outwardEndDate, setOutwardEndDate] = useState("");
  const [appliedOutwardStartDate, setAppliedOutwardStartDate] = useState("");
  const [appliedOutwardEndDate, setAppliedOutwardEndDate] = useState("");

  useEffect(() => {
    injectOnce();
    loadProducts();
    loadSalesAverageRates();

    try {
      const raw = localStorage.getItem("PriceListColorConfig_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setColorConfig((prev) => ({ ...prev, ...parsed }));
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (
      activeTab === "opening" ||
      activeTab === "inward" ||
      activeTab === "outward" ||
      activeTab === "closing"
    ) {
      setReportType(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      activeTab === "opening" ||
      activeTab === "inward" ||
      activeTab === "outward" ||
      activeTab === "closing"
    ) {
      loadReport(activeTab);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, products, productAvgSaleRateMap]);

  function getToken() {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("jwt") ||
      ""
    );
  }

  function formatNumber(val) {
    const n = Number(val);
    if (isNaN(n)) return val ?? "";
    return Number(n.toFixed(2));
  }

  function formatNumberFixed(val) {
    const n = Number(val);
    if (isNaN(n)) return "";
    return n.toFixed(2);
  }

  function N(v) {
    return isNaN(Number(v)) ? 0 : Number(v);
  }

  function getTitle(type) {
    if (type === "opening") return "OPENING STOCK REPORT";
    if (type === "inward") return "INWARD STOCK REPORT";
    if (type === "outward") return "OUTWARD STOCK REPORT";
    if (type === "closing") return "CLOSING STOCK REPORT";
    return "STOCK REPORT";
  }

  function getTotalKey(type) {
    if (type === "opening") return "total";
    if (type === "inward") return "totalPurchaseData";
    if (type === "outward") return "sTotal";
    if (type === "closing") return "closingTotal";
    return "total";
  }

  function getColumns(type) {
    if (type === "opening") {
      return [
        { label: "Product Name", key: "productName" },
        { label: "Category", key: "category" },
        { label: "SubCategory", key: "SubCategory" },
        { label: "WareHouse Name", key: "warehouseName" },
        { label: "HSN Code", key: "HSN_Code" },
        { label: "Opening Qty", key: "opening_Qty", numeric: true },
        { label: "Opening Rate", key: "openingRate", numeric: true },
        { label: "GST Rate", key: "gstRate", suffix: "%", numeric: true },
        { label: "Total", key: "total", numeric: true },
      ];
    }

    if (type === "inward") {
      return [
        { label: "Product Name", key: "productName" },
        { label: "Category", key: "category" },
        { label: "SubCategory", key: "SubCategory" },
        { label: "WareHouse Name", key: "warehouseName" },
        { label: "HSN Code", key: "HSN_Code" },
        { label: "Inward Qty", key: "pQty", numeric: true },
        { label: "Purchase Rate", key: "averagePurchaseRate", numeric: true },
        { label: "GST Rate", key: "GSTRate", numeric: true },
        { label: "Total", key: "totalPurchaseData", numeric: true },
      ];
    }

    if (type === "outward") {
      return [
        { label: "Product Name", key: "productName" },
        { label: "Category", key: "category" },
        { label: "SubCategory", key: "SubCategory" },
        { label: "WareHouse Name", key: "warehouseName" },
        { label: "HSN Code", key: "HSN_Code" },
        { label: "Date", key: "__rowDateText" },
        { label: "Outward Qty", key: "sQty", numeric: true },
        { label: "Sales Rate", key: "averageSalesRate", numeric: true },
        { label: "Basic Total", key: "__basicTotal", numeric: true },
        { label: "GST Rate", key: "GSTRate", numeric: true },
        { label: "Total", key: "sTotal", numeric: true },
      ];
    }

    return [
      { label: "Product Name", key: "Product_Title" },
      { label: "Category", key: "category" },
      { label: "SubCategory", key: "SubCategory" },
      { label: "WareHouse Name", key: "warehouseName" },
      { label: "HSN Code", key: "HSN_Code" },
      { label: "Avg Price", key: "closingAveRate", numeric: true },
      { label: "Closing Qty", key: "closingQty", numeric: true },
      { label: "Closing Rate", key: "closingAveRate", numeric: true },
      { label: "GST Rate", key: "GSTRate", numeric: true },
      { label: "Total", key: "closingTotal", numeric: true },
    ];
  }

  async function loadProducts() {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const db = userData?.database;
      if (!db) return;

      const token = getToken();
      const url = `${BASE_URL}${PurchaseProductList_Product}/${db}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();
      const list = Array.isArray(json?.Product) ? json.Product : [];
      setProducts(list);
    } catch (e) {
      console.log("Products load error:", e);
      setProducts([]);
    }
  }

  async function loadSalesAverageRates() {
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      if (!user?._id) {
        setProductAvgSaleRateMap({});
        return;
      }

      const res = await createOrderhistoryview(user?._id);
      const completed = Array.isArray(res?.orderHistory)
        ? res.orderHistory.filter((o) =>
            String(o?.status || "")
              .toLowerCase()
              .includes("completed"),
          )
        : [];

      const rateMap = {};
      const titleMap = {};

      for (let i = 0; i < completed.length; i++) {
        const ord = completed[i];
        const items = Array.isArray(ord?.orderItems) ? ord.orderItems : [];

        for (let j = 0; j < items.length; j++) {
          const it = items[j];
          const pid =
            it?.productId?._id ||
            it?.productId?.id ||
            it?.productId ||
            it?.ProductId ||
            it?.product_id ||
            "";

          const ptitle =
            it?.productId?.Product_Title || it?.Product_Title || "";

          const rate = N(it?.price);

          if (pid) {
            const key = `id:${String(pid)}`;
            if (!rateMap[key]) rateMap[key] = { sum: 0, count: 0 };
            rateMap[key].sum += rate;
            rateMap[key].count += 1;
          }

          if (ptitle) {
            const tKey = `title:${String(ptitle).trim().toLowerCase()}`;
            if (!titleMap[tKey]) titleMap[tKey] = { sum: 0, count: 0 };
            titleMap[tKey].sum += rate;
            titleMap[tKey].count += 1;
          }
        }
      }

      const finalMap = {};

      Object.keys(rateMap).forEach((k) => {
        const obj = rateMap[k];
        finalMap[k] =
          obj.count > 0 ? Number((obj.sum / obj.count).toFixed(2)) : 0;
      });

      Object.keys(titleMap).forEach((k) => {
        const obj = titleMap[k];
        finalMap[k] =
          obj.count > 0 ? Number((obj.sum / obj.count).toFixed(2)) : 0;
      });

      setProductAvgSaleRateMap(finalMap);
    } catch (e) {
      console.log("Sales average rate load error:", e);
      setProductAvgSaleRateMap({});
    }
  }

  function findCategoryAndSub(row) {
    let category = "";
    let sub = "";
    let profitPercentage = 0;

    const possibleId =
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "";

    if (possibleId) {
      for (let i = 0; i < products.length; i++) {
        if (String(products[i]?._id) === String(possibleId)) {
          category = products[i]?.category || "";
          sub = products[i]?.SubCategory || "";
          profitPercentage =
            Number(
              String(products[i]?.ProfitPercentage ?? 0).replace(
                /[^\d.-]/g,
                "",
              ),
            ) || 0;
          break;
        }
      }
      if (category || sub || profitPercentage) {
        return {
          category,
          SubCategory: sub,
          profitPercentage,
        };
      }
    }

    const title =
      row?.productName ||
      row?.Product_Title ||
      row?.productTitle ||
      row?.ProductName ||
      "";

    if (title) {
      const t = String(title).trim().toLowerCase();
      for (let i = 0; i < products.length; i++) {
        const pt = String(products[i]?.Product_Title || "")
          .trim()
          .toLowerCase();
        if (pt && pt === t) {
          category = products[i]?.category || "";
          sub = products[i]?.SubCategory || "";
          profitPercentage =
            Number(
              String(products[i]?.ProfitPercentage ?? 0).replace(
                /[^\d.-]/g,
                "",
              ),
            ) || 0;
          break;
        }
      }
    }

    return {
      category,
      SubCategory: sub,
      profitPercentage,
    };
  }

  function getRowDateValue(row) {
    const raw =
      row?.date ||
      row?.Date ||
      row?.createdAt ||
      row?.updatedAt ||
      row?.invoiceDate ||
      row?.orderDate ||
      row?.salesDate ||
      row?.saleDate ||
      row?.purchaseDate ||
      row?.billDate ||
      "";

    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d;
  }

  function getRowDateText(row) {
    const d = getRowDateValue(row);
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function getProductName(row) {
    return row?.productName || row?.Product_Title || "";
  }

  function findAverageSaleRateForRow(row) {
    const possibleId =
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "";

    if (possibleId) {
      const byId = productAvgSaleRateMap[`id:${String(possibleId)}`];
      if (byId !== undefined) return Number(byId);
    }

    const title =
      row?.productName ||
      row?.Product_Title ||
      row?.productTitle ||
      row?.ProductName ||
      "";

    if (title) {
      const byTitle =
        productAvgSaleRateMap[`title:${String(title).trim().toLowerCase()}`];
      if (byTitle !== undefined) return Number(byTitle);
    }

    return Number(row?.averageSalesRate || 0);
  }

  async function loadReport(type) {
    try {
      setLoading(true);
      setSearch("");
      setData([]);

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const financialYearSelected = JSON.parse(
        localStorage.getItem("financialYearSelected"),
      );
      const db = `${userData.database}-${financialYearSelected}`;
      if (!db) {
        setLoading(false);
        return;
      }

      const token = getToken();

      let endpoint = "";
      if (type === "opening") endpoint = "/product/view-opening-report";
      if (type === "inward") endpoint = "/purchase-order/invert-report-stock";
      if (type === "outward") endpoint = "/order/outward-stock-report";
      if (type === "closing") endpoint = "/stock-updation/stock-data";

      const url = `${BASE_URL}${endpoint}/${db}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();

      let list = [];
      if (type === "opening") list = json?.products || [];
      else list = json?.data || [];

      if (!Array.isArray(list)) list = [];

      const updated = [];
      for (let i = 0; i < list.length; i++) {
        const row = list[i];
        const info = findCategoryAndSub(row);

        let averageSalesRate = Number(row?.averageSalesRate || 0);
        let basicTotal = 0;

        if (type === "outward") {
          averageSalesRate = findAverageSaleRateForRow(row);
          basicTotal = Number((N(row?.sQty) * N(averageSalesRate)).toFixed(2));
        }

        updated.push({
          ...row,
          category: info.category,
          SubCategory: info.SubCategory,
          __profitPercentage: info.profitPercentage || 0,
          averageSalesRate:
            type === "outward"
              ? Number(averageSalesRate.toFixed(2))
              : row?.averageSalesRate,
          __basicTotal: type === "outward" ? basicTotal : 0,
          __rowDateText: getRowDateText(row),
        });
      }

      setData(updated);
      setLoading(false);
    } catch (e) {
      console.log("Report load error:", e);
      setData([]);
      setLoading(false);
    }
  }

  function getFilteredRows() {
    const q = String(search || "")
      .trim()
      .toLowerCase();
    const out = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      const name = String(getProductName(row) || "").toLowerCase();
      const hsn = String(row?.HSN_Code || "").toLowerCase();
      const wh = String(row?.warehouseName || "").toLowerCase();
      const cat = String(row?.category || "").toLowerCase();
      const sub = String(row?.SubCategory || "").toLowerCase();

      let searchOk = true;
      if (q) {
        searchOk =
          name.includes(q) ||
          hsn.includes(q) ||
          wh.includes(q) ||
          cat.includes(q) ||
          sub.includes(q);
      }

      let dateOk = true;
      if (
        reportType === "outward" &&
        (appliedOutwardStartDate || appliedOutwardEndDate)
      ) {
        const rowDate = getRowDateValue(row);

        if (!rowDate) {
          dateOk = false;
        } else {
          const rowOnly = new Date(
            rowDate.getFullYear(),
            rowDate.getMonth(),
            rowDate.getDate(),
          );

          if (appliedOutwardStartDate) {
            const start = new Date(appliedOutwardStartDate);
            const startOnly = new Date(
              start.getFullYear(),
              start.getMonth(),
              start.getDate(),
            );
            if (rowOnly < startOnly) dateOk = false;
          }

          if (appliedOutwardEndDate) {
            const end = new Date(appliedOutwardEndDate);
            const endOnly = new Date(
              end.getFullYear(),
              end.getMonth(),
              end.getDate(),
            );
            if (rowOnly > endOnly) dateOk = false;
          }
        }
      }

      if (searchOk && dateOk) out.push(row);
    }

    return out;
  }

  function getColumnTotal(filteredRows, key) {
    let total = 0;
    for (let i = 0; i < filteredRows.length; i++) {
      const v = Number(filteredRows[i]?.[key]);
      if (!isNaN(v)) total += v;
    }
    return total;
  }

  function getReportTotal(filteredRows) {
    const key = getTotalKey(reportType);
    return getColumnTotal(filteredRows, key);
  }

  function applyOutwardDateFilter() {
    setAppliedOutwardStartDate(outwardStartDate);
    setAppliedOutwardEndDate(outwardEndDate);
  }

  function clearOutwardDateFilter() {
    setOutwardStartDate("");
    setOutwardEndDate("");
    setAppliedOutwardStartDate("");
    setAppliedOutwardEndDate("");
  }

  function downloadPDF() {
    const cols = getColumns(reportType);
    const filteredRows = getFilteredRows();

    const doc = new jsPDF("landscape", "mm", "a4", false);
    doc.setFontSize(14);
    doc.text(getTitle(reportType), 14, 12);

    const head = [cols.map((c) => c.label)];
    const body = [];

    for (let i = 0; i < filteredRows.length; i++) {
      const row = filteredRows[i];
      const arr = [];
      for (let j = 0; j < cols.length; j++) {
        const col = cols[j];
        const val = row?.[col.key];
        if (col.numeric) arr.push(formatNumberFixed(val));
        else arr.push(`${val ?? ""}${col.suffix || ""}`);
      }
      body.push(arr);
    }

    const totalRow = [];
    for (let j = 0; j < cols.length; j++) {
      const col = cols[j];
      if (j === 0) {
        totalRow.push("GRAND TOTAL");
      } else if (col.numeric) {
        totalRow.push(formatNumberFixed(getColumnTotal(filteredRows, col.key)));
      } else {
        totalRow.push("");
      }
    }
    body.push(totalRow);

    autoTable(doc, {
      startY: 18,
      head: head,
      body: body,
      styles: { fontSize: 9, halign: "center" },
      headStyles: {
        fillColor: [242, 140, 51],
        textColor: 0,
        fontStyle: "bold",
      },
      didParseCell: function (cell) {
        if (cell.row.index === body.length - 1) {
          cell.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save(`${reportType}-stock-report.pdf`);
  }

  function downloadExcel() {
    const cols = getColumns(reportType);
    const filteredRows = getFilteredRows();

    const excelData = [];

    for (let i = 0; i < filteredRows.length; i++) {
      const row = filteredRows[i];
      const obj = {};
      for (let j = 0; j < cols.length; j++) {
        const col = cols[j];
        if (col.numeric) obj[col.label] = formatNumber(row?.[col.key]);
        else obj[col.label] = `${row?.[col.key] ?? ""}${col.suffix || ""}`;
      }
      excelData.push(obj);
    }

    const totalObj = {};
    for (let j = 0; j < cols.length; j++) {
      const col = cols[j];
      if (j === 0) totalObj[col.label] = "GRAND TOTAL";
      else if (col.numeric)
        totalObj[col.label] = formatNumber(
          getColumnTotal(filteredRows, col.key),
        );
      else totalObj[col.label] = "";
    }
    excelData.push(totalObj);

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    XLSX.writeFile(wb, `${reportType}-stock-report.xlsx`);
  }

  function getActiveIndex() {
    let idx = 0;
    for (let i = 0; i < TABS.length; i++) {
      if (TABS[i].key === activeTab) {
        idx = i;
        break;
      }
    }
    return idx;
  }

  function renderNonReportTab() {
    if (activeTab === "all") return <ClosingStockReport />;
    if (activeTab === "tab5") return <LowStockAlert />;
    if (activeTab === "tab6") return <DamagedStock />;
    if (activeTab === "tab7") return <OverDueStock />;
    if (activeTab === "tab8") return <StockDifferenceReport />;
    if (activeTab === "tab9") return <WareHouseOverDueStock />;
    if (activeTab === "tab10") return <DeadPartyReport />;
    if (activeTab === "tab11") return <Wastageproduction />;
    return null;
  }

  const isReportTab =
    activeTab === "opening" ||
    activeTab === "inward" ||
    activeTab === "outward" ||
    activeTab === "closing";

  if (!isReportTab) {
    const activeIndex = getActiveIndex();
    const tabWidthPct = 100 / (TABS.length || 1);

    return (
      <div style={{ padding: 0 }}>
        <div className="ul-shell">
          <div className="ul-tabsbar">
            <div className="ul-tabswrap">
              <div className="ul-tabs">
                <div
                  className="ul-indicator"
                  style={{
                    width: `${tabWidthPct}%`,
                    transform: `translateX(${activeIndex * 100}%)`,
                  }}
                />
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`ul-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ padding: 12 }}>{renderNonReportTab()}</div>
        </div>
      </div>
    );
  }

  const cols = getColumns(reportType);
  const filteredRows = getFilteredRows();
  const totalValue = getReportTotal(filteredRows);
  const activeIndex = getActiveIndex();
  const tabWidthPct = 100 / (TABS.length || 1);

  return (
    <div style={{ padding: 0 }}>
      <div className="ul-shell">
        {/* TAB BAR */}
        <div className="ul-tabsbar">
          <div className="ul-tabswrap">
            <div className="ul-tabs">
              <div
                className="ul-indicator"
                style={{
                  width: `${tabWidthPct}%`,
                  transform: `translateX(${activeIndex * 100}%)`,
                }}
              />
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`ul-tab ${activeTab === t.key ? "active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* BODY */}
        <div style={{ padding: 12 }}>
          {/* TOOLBAR */}
          <Card
            style={{
              padding: "10px",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  textTransform: "uppercase",
                  fontSize: 16,
                }}
              >
                {getTitle(reportType)}{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ fontWeight: 900 }}>
                Total: ₹ {formatNumber(totalValue)}
              </div>

              <div style={{ flex: 1 }} />

              <div style={{ width: 360, minWidth: 220 }}>
                <Input
                  placeholder="Search Product / HSN / Warehouse / Category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {reportType === "outward" && (
                <div className="stock-filter-box">
                  <div>
                    <Label className="mb-0" style={{ fontSize: 12 }}>
                      Start Date
                    </Label>
                    <Input
                      type="date"
                      value={outwardStartDate}
                      onChange={(e) => setOutwardStartDate(e.target.value)}
                      style={{ height: 34, background: "#fff", minWidth: 140 }}
                    />
                  </div>

                  <div>
                    <Label className="mb-0" style={{ fontSize: 12 }}>
                      End Date
                    </Label>
                    <Input
                      type="date"
                      value={outwardEndDate}
                      onChange={(e) => setOutwardEndDate(e.target.value)}
                      style={{ height: 34, background: "#fff", minWidth: 140 }}
                    />
                  </div>

                  <Button
                    color="primary"
                    onClick={applyOutwardDateFilter}
                    style={{ height: 34 }}
                  >
                    Submit
                  </Button>

                  <Button
                    color="secondary"
                    onClick={clearOutwardDateFilter}
                    style={{ height: 34 }}
                  >
                    Clear
                  </Button>
                </div>
              )}

              <Button
                color="primary"
                onClick={() => loadReport(activeTab)}
                style={{ height: 34 }}
              >
                Reload
              </Button>

              <Button
                color="secondary"
                onClick={downloadPDF}
                style={{ height: 34 }}
              >
                PDF
              </Button>

              <Button
                color="success"
                onClick={downloadExcel}
                style={{ height: 34 }}
              >
                XLSX
              </Button>
            </div>
          </Card>

          {/* TABLE */}
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            <div className="sr-table-wrap">
              <table className="sr-table">
                <thead>
                  <tr>
                    {cols.map((c, i) => (
                      <th key={i}>{c.label}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={cols.length}
                        style={{ textAlign: "center", padding: 18 }}
                      >
                        <Spinner size="sm" /> Loading...
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={cols.length}
                        style={{
                          textAlign: "center",
                          padding: 18,
                          color: "#6b7280",
                        }}
                      >
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredRows.map((row, rIdx) => (
                        <tr
                          key={rIdx}
                          style={{
                            background: rIdx % 2 === 0 ? "#f9f9f9" : "#fff",
                          }}
                        >
                          {cols.map((col, cIdx) => {
                            const isName =
                              col.key === "productName" ||
                              col.key === "Product_Title";
                            const raw = row?.[col.key];
                            const text = col.numeric
                              ? formatNumberFixed(raw)
                              : `${raw ?? ""}${col.suffix || ""}`;

                            return (
                              <td
                                key={cIdx}
                                title={isName ? String(raw || "") : ""}
                                style={{
                                  maxWidth: isName ? 240 : "auto",
                                  whiteSpace: isName ? "nowrap" : "normal",
                                  overflow: isName ? "hidden" : "visible",
                                  textOverflow: isName ? "ellipsis" : "unset",
                                  color: isName
                                    ? getProfitColor(
                                        row?.__profitPercentage,
                                        colorConfig,
                                      )
                                    : undefined,
                                  fontWeight: isName ? 700 : 400,
                                }}
                              >
                                {text}
                              </td>
                            );
                          })}
                        </tr>
                      ))}

                      {/* GRAND TOTAL ROW */}
                      <tr style={{ fontWeight: 900, background: "#f1f5f9" }}>
                        {cols.map((col, idx) => {
                          if (idx === 0) return <td key={idx}>GRAND TOTAL</td>;
                          if (col.numeric)
                            return (
                              <td key={idx}>
                                {formatNumberFixed(
                                  getColumnTotal(filteredRows, col.key),
                                )}
                              </td>
                            );
                          return <td key={idx}></td>;
                        })}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
